
import { Injectable, OnDestroy } from '@angular/core';
import { Client, Message, over, StompSubscription } from '@stomp/stompjs';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import SockJS from 'sockjs-client';
import { AppSettings } from 'src/app/services/AppSettings';

export enum SocketClientState {
  ATTEMPTING, CONNECTED, DISCONNECTED
}

interface ConnectionDetail {
  timestamp: Date;
  state: SocketClientState;
  client?: Client;
}

@Injectable({
  providedIn: 'root'
})
export class SocketService implements OnDestroy {

  private client: Client;
  private state: BehaviorSubject<SocketClientState>;
  private subscribedTopics: Set<string>;
  private reconnectInterval: any;
  private connectionList: ConnectionDetail[] = [];
  private activeClient: Client | null = null;

  constructor() {
    this.state = new BehaviorSubject<SocketClientState>(SocketClientState.ATTEMPTING);
    this.subscribedTopics = new Set<string>();
    // this.setupNetworkListeners();
    this.disconnectAllClients();
    this.initiateConnection();
  }

  private setupNetworkListeners() {
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    // console.log('Network listeners set up.');
  }
  private handleOnline() {
    if (!this.client.connected && !this.reconnectInterval) {
      setTimeout(() => this.reconnect(), 2000); // Add a delay to prevent spamming
    }
  }


  private handleOffline() {
    // console.log('Network disconnected.');
    this.state.next(SocketClientState.DISCONNECTED);
  }

  public initiateConnection() {
    this.client = over(() => new SockJS(AppSettings.webSocketUrl));
    // this.client.debug = (msg: string) => console.log(new Date(), msg);
    this.client.connect({}, () => {
      // console.log('WebSocket connection established');
      this.state.next(SocketClientState.CONNECTED);
      this.addConnectionDetail(SocketClientState.CONNECTED, this.client);
      this.activeClient = this.client;
      this.reSubscribeTopics();
    });
    this.client.reconnect_delay = 10000;
    this.client.heartbeat.incoming = 4000;
    this.client.heartbeat.outgoing = 4000;
  }



  public reconnect() {
    if (this.state.getValue() !== SocketClientState.CONNECTED) {
      if (!this.reconnectInterval) {
        this.reconnectInterval = setInterval(() => {
          if (this.client && !this.client.connected) {
            console.log('Attempting to reconnect...');
            this.disconnectClient();
            this.initiateConnection();
          } else {
            console.log('Reconnection successful. Clearing interval...');
            clearInterval(this.reconnectInterval);
            this.reconnectInterval = undefined;
          }
        }, 5000);
      }
    }
  }



  public disconnectClient() {
    if (this.client && this.client.connected) {
      this.client.disconnect(() => {
        // console.log('WebSocket connection deactivated');
        this.state.next(SocketClientState.DISCONNECTED);
        this.addConnectionDetail(SocketClientState.DISCONNECTED, this.client);
      });
    } else {
      if (this.client.ws) {
        // console.log('Force closing WebSocket connection...');
        this.client.ws.onclose = () => {}; // Disable onclose handler temporarily
        this.client.ws.close();
        this.state.next(SocketClientState.DISCONNECTED);
        this.addConnectionDetail(SocketClientState.DISCONNECTED, this.client);
      }
    }
    clearInterval(this.reconnectInterval);
  }

  public disconnectAllClients() {
    if (this.connectionList.length > 0) {
      this.connectionList.forEach((connection) => {
        if (connection.client && connection.client.connected) {
          // console.log(`Disconnecting connection at ${connection.timestamp}`);
          connection.client.disconnect(() => {
            this.addConnectionDetail(SocketClientState.DISCONNECTED, connection.client);
          });
        }
      });
      this.connectionList = [];
      this.activeClient = null;
    }
  }

  clearTopices() {
    this.subscribedTopics.clear();
  }

  public reSubscribeTopics() {
    if (this.subscribedTopics.size > 0) {
      console.log(`Resubscribing to ${this.subscribedTopics.size} topics...`);
      const topics = Array.from(this.subscribedTopics);
      this.subscribedTopics.clear();
      topics.forEach(topic => {
        if (!this.subscribedTopics.has(topic)) {
          this.subscribedTopicss(topic);
        }
      });
    } else {
      console.log('No topics to resubscribe to.');
    }
  }


  public subscribedTopicss(topic: string, handler = SocketService.jsonHandler) {
    if (this.subscribedTopics.has(topic)) {
      // console.log(`Already subscribed to topic: ${topic}`);
      return;
    }

    const subscription: StompSubscription = this.client.subscribe(topic, message => {
      handler(message);
    });

    this.subscribedTopics.add(topic);
    // console.log(`Subscribed to topic: ${topic}`);
    return () => {
      if (subscription.id) {
        this.client.unsubscribe(subscription.id);
        this.subscribedTopics.delete(topic);
        console.log(`Unsubscribed from topic: ${topic}`);
      }
    };
  }

  reloadconnection() {
    let status: any = {};
    status.connected = this.client.connected;
    status.streamTopic = this.state.observers.length;
    return status;
  }

  connect(): Observable<Client> {
    return new Observable<Client>(observer => {
      this.state.pipe(filter(state => state === SocketClientState.CONNECTED)).subscribe(() => {
        observer.next(this.client);
      });
    });
  }

  ngOnDestroy() {
    this.disconnectClient();
    this.subscribedTopics.clear();
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
    }
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    // console.log('SocketService destroyed and cleaned up.');
  }

  onMessage(topic: string, handler = SocketService.jsonHandler): Observable<any> {
    return this.connect().pipe(
      first(),
      switchMap(client => new Observable<any>(observer => {
        if (!this.subscribedTopics.has(topic)) {
          const subscription: StompSubscription = client.subscribe(topic, message => {
            observer.next(handler(message));
          });
          this.subscribedTopics.add(topic);
          // console.log(`Subscribed to topic: ${topic}`);
          return () => {
            client.unsubscribe(subscription.id);
            this.subscribedTopics.delete(topic);
            // console.log(`Unsubscribed from topic: ${topic}`);
          };
        } else {
          // console.log(`Already subscribed to topic: ${topic}`);
          observer.complete();
        }
      }))
    );
  }

  onPlainMessage(topic: string): Observable<string> {
    return this.onMessage(topic, SocketService.textHandler);
  }

  send(topic: string, payload: any): void {
    this.connect()
      .pipe(first())
      .subscribe(inst => inst.send('/careonline' + topic, {}, JSON.stringify(payload)));
  }

  static jsonHandler(message: Message): any {
    return JSON.parse(message.body);
  }

  static textHandler(message: Message): string {
    return message.body;
  }

  getSubscribedTopics(): string[] {
    return Array.from(this.subscribedTopics);
  }

  private addConnectionDetail(state: SocketClientState, client: Client) {
    this.connectionList.push({ timestamp: new Date(), state, client });
  }

  public disconnectAllClientsExceptLatest() {
    if (this.connectionList.length != 0) {
      // console.log("stream", this.connectionList);
      const latestConnection = this.connectionList[this.connectionList.length - 1];
      this.connectionList.forEach((connection) => {
        // console.log(connection);
        if (connection !== latestConnection && connection.client) {
          // console.log(`Disconnecting connection at ${connection.timestamp}`);
          connection.client.disconnect(() => {
            this.addConnectionDetail(SocketClientState.DISCONNECTED, connection.client);
          });
        }
      });
      this.connectionList = [latestConnection];
      this.activeClient = latestConnection.client || null;
      // console.log('All other connections disconnected. Only the latest connection remains active.');
    }
  }

  getConnectionList(): ConnectionDetail[] {
    return this.connectionList;
  }

  getActiveClient(): Client | null {
    return this.activeClient;
  }

}

